/**
 * Normalize drink names for display. ABC/Utah imports often arrive in ALL CAPS;
 * US-facing UI should use title case, keeping common beer/spirits acronyms.
 */

const KEEP_UPPER = new Set([
  'IPA',
  'IIPA',
  'DIPA',
  'NEIPA',
  'DDH',
  'II',
  'III',
  'IV',
  'BBQ',
  'NY',
  'LA',
  'USA',
  'NA',
  'ABV',
  'VS',
  'XO',
  'VSOP',
  'KPM',
  'OG',
  'NFC',
  'CBC',
  'APA',
]);

function capitalizeWord(word: string): string {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (KEEP_UPPER.has(upper)) return upper;
  // Preserve patterns like "60Minute" after split fails — handle digit+letter
  if (/^\d/.test(word)) {
    return word.replace(/[a-zA-Z].*$/, (rest) => {
      const u = rest.toUpperCase();
      if (KEEP_UPPER.has(u)) return u;
      return rest.charAt(0).toUpperCase() + rest.slice(1).toLowerCase();
    });
  }
  if (word.includes("'")) {
    return word
      .split("'")
      .map((part, i) => {
        if (!part) return part;
        if (i > 0 && part.length <= 1) return part.toLowerCase();
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join("'");
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/** True when the name is shouty ALL CAPS (typical of some state price lists). */
export function isAllCapsName(name: string): boolean {
  const letters = name.replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return false;
  const upper = [...letters].filter((c) => c >= 'A' && c <= 'Z').length;
  return upper / letters.length >= 0.85;
}

export function formatDrinkDisplayName(name: string): string {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed || !isAllCapsName(trimmed)) return trimmed;
  return trimmed
    .toLowerCase()
    .split(/(\s+|[-/])/)
    .map((token) => {
      if (!token || /^\s+$/.test(token) || token === '-' || token === '/') {
        return token;
      }
      return capitalizeWord(token);
    })
    .join('');
}
